import { UnreachableCaseError } from '@errors/unreachable';
import type {
  AutoConfiguratorState,
  BooleanValue,
  DecimalValue,
  DescriptionValue,
  NumberValue,
  SelectValue,
  TextValue,
  ValueId,
} from '@snap-types/models/auto-configurator/state';

import { getLineItemValue } from '../line-item';
import { getQuantityBreakValue } from '../quantity-break';

export function getValue(state: AutoConfiguratorState, valueId: ValueId) {
  const { model } = valueId;

  switch (model) {
    case 'lineItem':
      return getLineItemValue(state, valueId);

    case 'quantityBreak':
      return getQuantityBreakValue(state, valueId);

    /* v8 ignore next 2 */
    default:
      throw new UnreachableCaseError(model);
  }
}

export function getDecimalValue(
  state: AutoConfiguratorState,
  valueId: ValueId,
): DecimalValue {
  const value = getValue(state, valueId);

  if (value.type !== 'decimal') {
    throw new Error(
      `Expected value for "${valueId.model}.${valueId.key}" to have type "decimal", received: "${value.type}"`,
    );
  }

  return value;
}

export function getNumberValue(
  state: AutoConfiguratorState,
  valueId: ValueId,
): NumberValue {
  const value = getValue(state, valueId);

  if (value.type !== 'number') {
    throw new Error(
      `Expected value for "${valueId.model}.${valueId.key}" to have type "number", received: "${value.type}"`,
    );
  }

  return value;
}

export function getSelectValue(
  state: AutoConfiguratorState,
  valueId: ValueId,
): SelectValue {
  const value = getValue(state, valueId);

  if (value.type !== 'select') {
    throw new Error(
      `Expected value for "${valueId.model}.${valueId.key}" to have type "select", received: "${value.type}"`,
    );
  }

  return value;
}

export function getSelectChoiceValue(
  state: AutoConfiguratorState,
  valueId: ValueId,
): string | null {
  const selectValue = getSelectValue(state, valueId);

  return selectValue.value?.value ?? null;
}

export function getTextValue(
  state: AutoConfiguratorState,
  valueId: ValueId,
): TextValue {
  const value = getValue(state, valueId);

  if (value.type !== 'text') {
    throw new Error(
      `Expected value for "${valueId.model}.${valueId.key}" to have type "text", received: "${value.type}"`,
    );
  }

  return value;
}

export function getDescriptionValue(
  state: AutoConfiguratorState,
  valueId: ValueId,
): DescriptionValue {
  const value = getValue(state, valueId);

  if (value.type !== 'description') {
    throw new Error(
      `Expected value for "${valueId.model}.${valueId.key}" to have type "description", received: "${value.type}"`,
    );
  }

  return value;
}

export function getBooleanValue(
  state: AutoConfiguratorState,
  valueId: ValueId,
): BooleanValue {
  const value = getValue(state, valueId);

  if (value.type !== 'boolean') {
    throw new Error(
      `Expected value for "${valueId.model}.${valueId.key}" to have type "boolean", received: "${value.type}"`,
    );
  }

  return value;
}
